import { useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { IEventRsvp, RsvpStatus } from '../../Interfaces/IEventInteraction';
import { useAuthenticatedImage } from '../../hooks/useAuthenticatedImage';

interface EventRsvpTabsProps {
    rsvps: IEventRsvp[];
}

const RSVP_ORDER: RsvpStatus[] = ['yes', 'no', 'maybe'];

const RSVP_TITLES: Record<RsvpStatus, string> = {
    yes: 'Zusagen',
    no: 'Absagen',
    maybe: 'Vielleicht',
};

const tabLabel = (status: RsvpStatus, entries: IEventRsvp[]): string => {
    if (status !== 'yes') {
        return `${RSVP_TITLES[status]} (${entries.length})`;
    }
    const guests = entries.reduce((total, rsvp) => total + rsvp.guest_count, 0);
    if (guests === 0) {
        return `Zusagen (${entries.length})`;
    }
    return `Zusagen (${entries.length}) · ${entries.length + guests} Teilnehmer`;
};

const formatDisplayName = (rsvp: IEventRsvp): string => {
    const fullName = `${rsvp.user.first_name ?? ''} ${rsvp.user.last_name ?? ''}`.trim()
        || rsvp.user.username;
    return rsvp.guest_count > 0 ? `${fullName} +${rsvp.guest_count}` : fullName;
};

const formatRsvpDate = (isoString?: string): string => {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(d);
};

interface RsvpParticipantChipProps {
    displayName: string;
    isMobile: boolean;
    userId: number;
    rsvpAt?: string;
}

const RsvpParticipantChip: React.FC<RsvpParticipantChipProps> = ({
    displayName,
    isMobile,
    userId,
    rsvpAt,
}) => {
    const avatarUrl = `/api/users/${userId}/avatar`;
    const { src, isLoading } = useAuthenticatedImage(avatarUrl);
    const dateLabel = formatRsvpDate(rsvpAt);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                px: 1.25,
                py: 0.75,
                minWidth: isMobile ? '100%' : 180,
                maxWidth: '100%',
            }}
        >
            <Avatar src={src} sx={{ width: 32, height: 32, flexShrink: 0 }}>
                {isLoading ? <Skeleton variant="circular" width={32} height={32} /> : displayName.slice(0, 2).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="body2"
                    sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.2 }}
                >
                    {displayName}
                </Typography>
                {dateLabel && (
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        {dateLabel}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const EventRsvpTabs: React.FC<EventRsvpTabsProps> = ({ rsvps }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeTab, setActiveTab] = useState<number>(0);

    const grouped = useMemo<Record<RsvpStatus, IEventRsvp[]>>(() => ({
        yes: rsvps.filter((rsvp) => rsvp.status === 'yes'),
        no: rsvps.filter((rsvp) => rsvp.status === 'no'),
        maybe: rsvps.filter((rsvp) => rsvp.status === 'maybe'),
    }), [rsvps]);

    const activeStatus = RSVP_ORDER[activeTab];

    const getName = (rsvp: IEventRsvp): string => formatDisplayName(rsvp);

    return (
        <Box sx={{ minWidth: 0 }}>
            <Tabs
                value={activeTab}
                onChange={(_event, value: number) => setActiveTab(value)}
                variant={isMobile ? 'scrollable' : 'fullWidth'}
                allowScrollButtonsMobile
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}
            >
                {RSVP_ORDER.map((status) => (
                    <Tab
                        key={status}
                        label={tabLabel(status, grouped[status])}
                        sx={{ minHeight: 44 }}
                    />
                ))}
            </Tabs>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                {grouped[activeStatus].map((rsvp) => {
                    const displayName = getName(rsvp);

                    return (
                        <RsvpParticipantChip
                            key={`${activeStatus}-${rsvp.user.id}`}
                            userId={rsvp.user.id}
                            displayName={displayName}
                            isMobile={isMobile}
                            rsvpAt={rsvp.updated_at || rsvp.created_at}
                        />
                    );
                })}
                {grouped[activeStatus].length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
                        Noch keine Einträge.
                    </Typography>
                )}
            </Stack>

            {activeStatus === 'yes' && (() => {
                const guests = grouped.yes.reduce((total, rsvp) => total + rsvp.guest_count, 0);
                if (guests === 0) {
                    return null;
                }
                return (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {grouped.yes.length + guests} Teilnehmer, davon {guests}{' '}
                        {guests === 1 ? 'Gast' : 'Gäste'}
                    </Typography>
                );
            })()}
        </Box>
    );
};

export default EventRsvpTabs;
